import React from 'react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface ChartCardProps {
  title: string;
  type: 'line' | 'area' | 'bar' | 'pie';
  data: any[];
  className?: string;
}

const ChartCard: React.FC<ChartCardProps> = ({ title, type, data, className = '' }) => {
  const renderLegend = () => {
    if (type === 'line' && title === 'Response Time Latency') {
      return (
        <div className="chart-legend">
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: '#10b981' }}></div>
            <span>50th Percentile</span>
          </div>
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: '#3b82f6' }}></div>
            <span>95th Percentile</span>
          </div>
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: '#f97316' }}></div>
            <span>99th Percentile</span>
          </div>
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: '#eab308' }}></div>
            <span>Average</span>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    switch (type) {
      case 'line':
        if (title === 'Response Time Latency') {
          // Latency chart with multiple percentile lines
          return (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="time" 
                  stroke="#9ca3af" 
                  fontSize={12}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis 
                  stroke="#9ca3af" 
                  fontSize={12}
                  axisLine={false}
                  tickLine={false}
                  label={{ value: 'ms', angle: 0, position: 'insideTopLeft' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937', 
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    fontSize: '12px'
                  }}
                  labelStyle={{ color: '#f9fafb' }}
                />
                {/* P50 - Green line */}
                <Line 
                  type="linear" 
                  dataKey="p50" 
                  stroke="#10b981" 
                  strokeWidth={1.5} 
                  dot={false}
                  name="50th Percentile"
                  strokeLinecap="square"
                  strokeLinejoin="miter"
                />
                {/* P95 - Blue line */}
                <Line 
                  type="linear" 
                  dataKey="p95" 
                  stroke="#3b82f6" 
                  strokeWidth={1.5} 
                  dot={false}
                  name="95th Percentile"
                  strokeLinecap="square"
                  strokeLinejoin="miter"
                />
                {/* P99 - Orange line (like in your image) */}
                <Line 
                  type="linear" 
                  dataKey="p99" 
                  stroke="#f97316" 
                  strokeWidth={2} 
                  dot={false}
                  name="99th Percentile"
                  strokeLinecap="square"
                  strokeLinejoin="miter"
                />
                {/* Average - Yellow line */}
                <Line 
                  type="linear" 
                  dataKey="avg" 
                  stroke="#eab308" 
                  strokeWidth={1.5} 
                  dot={false}
                  name="Average"
                  strokeLinecap="square"
                  strokeLinejoin="miter"
                />
              </LineChart>
            </ResponsiveContainer>
          );
        } else {
          // Generic line chart for other data (like calls over time)
          return (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="time" 
                  stroke="#9ca3af" 
                  fontSize={12}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis 
                  stroke="#9ca3af" 
                  fontSize={12}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937', 
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    fontSize: '12px'
                  }}
                  labelStyle={{ color: '#f9fafb' }}
                />
                <Line 
                  type="linear" 
                  dataKey="calls" 
                  stroke="#10b981" 
                  strokeWidth={2} 
                  dot={false}
                  name="Calls"
                  strokeLinecap="square"
                  strokeLinejoin="miter"
                />
              </LineChart>
            </ResponsiveContainer>
          );
        }
      
      case 'area':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="time" stroke="#9ca3af" fontSize={12} axisLine={false} tickLine={false} />
              <YAxis stroke="#9ca3af" fontSize={12} axisLine={false} tickLine={false} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1f2937', 
                  border: '1px solid #374151',
                  borderRadius: '6px'
                }} 
              />
              <Area type="monotone" dataKey="avg" stackId="1" stroke="#6366f1" fill="#6366f1" fillOpacity={0.6} />
            </AreaChart>
          </ResponsiveContainer>
        );
      
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="name" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1f2937', 
                  border: '1px solid #374151',
                  borderRadius: '6px'
                }} 
              />
              <Bar dataKey="views" fill="#6366f1" />
              <Bar dataKey="conversions" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'pie':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                outerRadius={60}
                dataKey="value"
                label={({ name, percent }: any) => `${name} ${((percent as number || 0) * 100).toFixed(0)}%`}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1f2937', 
                  border: '1px solid #374151',
                  borderRadius: '6px'
                }} 
              />
            </PieChart>
          </ResponsiveContainer>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className={`chart-card ${className}`}>
      <div className="chart-header">
        <h3 className="chart-title">{title}</h3>
        {renderLegend()}
      </div>
      <div className="chart-content">
        {renderChart()}
      </div>
    </div>
  );
};

export default ChartCard;
